import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material";
import Rating from "@mui/material/Rating";
import CallOutlined from "@mui/icons-material/CallOutlined";

import EditOutlined from "@mui/icons-material/EditOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import FacebookOutlined from "@mui/icons-material/FacebookOutlined";
import Instagram from "@mui/icons-material/Instagram";
import Twitter from "@mui/icons-material/Twitter";
import LinkedIn from "@mui/icons-material/LinkedIn";
import DialogEditVenueIndividualContacts from "./DialogEditVenueIndividualContacts";

import EditCompanyContact from "../../contacts/components/EditCompanyContact";
import { IContactData } from "./DialogEditVenueIndividualContacts";
import { WhatsApp } from "@mui/icons-material";

interface ContactDetailsDialogProps {
  open: boolean;
  contact: any | null;
  onClose: () => void;
  onCancel?: () => void;
  onEdit?: (updatedContact: any) => void;
  onHoverChange?: (hovering: boolean, isEditing?: boolean) => void;
}

/* ---------- Main Dialog ---------- */
const ContactDetailsDialog: React.FC<ContactDetailsDialogProps> = ({
  open,
  contact,
  onClose,
  onEdit,
  onHoverChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentContact, setCurrentContact] = useState(contact);
  useEffect(() => {
    setCurrentContact(contact);
  }, [contact, open]);

  if (!contact) return null;

  const handleEditClick = () => {
    setCurrentContact(contact);
    setIsEditing(true);
  };

  const handleEditComplete = (updatedContact: IContactData) => {
    setIsEditing(false);
    setCurrentContact(updatedContact);
    if (onEdit) onEdit(updatedContact);
  };

  const handleDialogClose = () => {
    setIsEditing(false);
    onClose();
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setCurrentContact(contact);
  };
  const toTitleCase = (str?: string | null) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      PaperProps={{
        onMouseEnter: () => onHoverChange?.(true, isEditing),
        onMouseLeave: () => onHoverChange?.(false, isEditing),
      }}
      fullWidth
      maxWidth={isEditing ? "md" : "xs"}
      sx={{
        "& .MuiDialog-paper": { width: isEditing ? 600 : 330, maxWidth: "90%" },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          {isEditing ? "Edit Contact" : "Contact Details"}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {isEditing && currentContact ? (
          <DialogEditVenueIndividualContacts
            contact={currentContact}
            onComplete={handleEditComplete}
            onCancel={handleCancelEdit}
          />
        ) : (
          <>
            <Box
              sx={{
                borderRadius: 3,
                boxShadow: 0,
                p: 2,
                minHeight: 300,
                bgcolor: "background.default",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                textAlign: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: "auto",
                  mb: 2,
                  bgcolor: currentContact?.contact_image
                    ? "transparent"
                    : "primary.main",
                }}
                src={currentContact?.contact_image || undefined}
              >
                {!currentContact?.contact_image && (
                  <PersonOutline sx={{ fontSize: 42 }} />
                )}
              </Avatar>

              {/* Full name */}
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                {`${toTitleCase(currentContact?.first_name)} ${toTitleCase(
                  currentContact?.last_name
                )}`}
              </Typography>

              {/* Email */}
              {currentContact?.email && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {currentContact.email}
                </Typography>
              )}

              {/* Phone */}
              {currentContact?.phone && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {currentContact.phone}
                </Typography>
              )}

              {/* Contact type */}
              <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
                {currentContact?.contact_type === "company"
                  ? "Company Contact"
                  : "Individual Contact"}
              </Typography>

              <Divider sx={{ my: 1.5, opacity: 0.2 }} />

              {/* Action buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                {currentContact?.phone && (
                  <IconButton
                    sx={{
                      color: "primary.main",
                      bgcolor: "transparent",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() =>
                      window.open(
                        `https://wa.me/${currentContact.phone.replace(
                          /\D/g,
                          ""
                        )}`,
                        "_blank"
                      )
                    }
                  >
                    <WhatsApp />
                  </IconButton>
                )}

                {currentContact?.email && (
                  <IconButton
                    sx={{
                      color: "primary.main",
                      bgcolor: "transparent",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() =>
                      window.open(`mailto:${currentContact.email}`)
                    }
                  >
                    <EmailOutlined />
                  </IconButton>
                )}

                {currentContact?.phone && (
                  <IconButton
                    sx={{
                      color: "primary.main",
                      bgcolor: "transparent",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => window.open(`tel:${currentContact.phone}`)}
                  >
                    <CallOutlined />
                  </IconButton>
                )}
              </Box>

              {/* Edit button */}
              <Button
                variant="contained"
                size="small"
                sx={{
                  mt: 1,
                  textTransform: "capitalize",
                }}
                onClick={handleEditClick}
              >
                Edit
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactDetailsDialog;
